// import components
import { GridColDef } from '@mui/x-data-grid-pro';

// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import queries & mutations
import { useGetBrTypeOptions, useGetBrCategoryOptions } from 'queries';

// import types
import { PlanUnit } from '../types';

// import utils
import { dateFormatter, nestedFieldValueGetter } from 'utils/grid';

const detailsValueGetter = (row: PlanUnit, field: string) => row[field];

export const useGetColumns = ({
  hasReferenceDate = false,
  showFullTable = true
}: {
  hasReferenceDate: boolean;
  showFullTable?: boolean;
}): GridColDef[] => {
  const { t } = useTranslation();

  const { data: brTypeOptions } = useGetBrTypeOptions();
  const { data: brCategoryOptions } = useGetBrCategoryOptions();

  return useMemo(
    () =>
      [
        ...(!hasReferenceDate
          ? [
              {
                field: 'name',
                type: 'string',
                headerName: t('costCenters.grid.name'),
                valueGetter: (_, row: PlanUnit) => row.name,
                flex: 1,
                minWidth: 150
              }
            ]
          : []),
        ...(showFullTable
          ? [
              {
                field: 'code',
                type: 'string',
                headerName: t('costCenters.grid.code'),
                hideable: false,
                valueGetter: (_, row: PlanUnit) =>
                  detailsValueGetter(row, 'code'),
                minWidth: 80
              },
              {
                field: 'nameShort',
                type: 'string',
                headerName: t('costCenters.grid.nameShort'),
                valueGetter: (_, row: PlanUnit) =>
                  detailsValueGetter(row, 'nameShort'),
                flex: 1,
                minWidth: 150
              },
              {
                field: 'valid.from',
                type: 'date',
                headerName: t('validFrom'),
                valueGetter: (_, row: PlanUnit) =>
                  nestedFieldValueGetter('valid.from', row),
                valueFormatter: dateFormatter,
                flex: 1,
                maxWidth: 100
              },
              {
                field: 'valid.to',
                type: 'date',
                headerName: t('validTo'),
                valueGetter: (_, row: PlanUnit) =>
                  nestedFieldValueGetter('valid.to', row),
                valueFormatter: dateFormatter,
                flex: 1,
                maxWidth: 100
              }
            ]
          : [])
      ] as GridColDef[],
    [t, brTypeOptions, brCategoryOptions, hasReferenceDate, showFullTable]
  );
};
